import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Chip,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import PaidIcon from '@mui/icons-material/Paid';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';

const API_BASE_URL = config.API_BASE_URL;
const BASE_PATH = '/api/v1/content/admin/ibo-spending-report';

const STREAM_COLORS = { ticket: '#1976d2', content: '#00bfa5', subscription: '#ffb300' };
const TOTAL_COLOR = '#2e7d32';

// Default to the previous calendar month, formatted YYYY-MM for the <input type="month">.
function lastMonthKey() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(key) {
  if (!key) return '';
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const truncate = (s, n = 16) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * DV-255: Monthly per-IBO spending report. Admin picks a month and views/downloads
 * each registered IBO's gross spend across event tickets + content + subscriptions.
 * Tickets are bucketed by event date; content & subscriptions by charge date.
 * KPI cards + charts summarise the month; clicking a row opens the itemised drill-down.
 */
export default function IboSpendingReportPage() {
  const theme = useTheme();
  const { getAccessToken } = useAuth();
  const [month, setMonth] = useState(lastMonthKey());
  const [rows, setRows] = useState([]);
  const [loadedMonth, setLoadedMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'csv' | 'excel' | null
  const [error, setError] = useState('');

  // Drill-down dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailIbo, setDetailIbo] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error('Authentication token not available');
    return { Authorization: `Bearer ${token}` };
  }, [getAccessToken]);

  const loadReport = useCallback(async () => {
    if (!month) return;
    setLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}${BASE_PATH}?month=${encodeURIComponent(month)}`, { headers });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`${res.status} ${text}`);
      }
      const json = await res.json().catch(() => ({}));
      const data = json.data !== undefined ? json.data : json;
      setRows(Array.isArray(data) ? data : []);
      setLoadedMonth(month);
    } catch (e) {
      setError(e.message || 'Failed to load report');
      setRows([]);
      setLoadedMonth('');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, month]);

  const downloadReport = useCallback(async (format) => {
    if (!month) return;
    setDownloading(format);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${API_BASE_URL}${BASE_PATH}/export?month=${encodeURIComponent(month)}&format=${format}`,
        { headers }
      );
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`${res.status} ${text}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ibo-spending-${month}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Download failed');
    } finally {
      setDownloading(null);
    }
  }, [authHeaders, month]);

  const openDetail = useCallback(
    async (iboNumber) => {
      setDetailOpen(true);
      setDetailIbo(iboNumber);
      setDetail(null);
      setDetailError('');
      setDetailLoading(true);
      try {
        const headers = await authHeaders();
        const res = await fetch(
          `${API_BASE_URL}${BASE_PATH}/${encodeURIComponent(iboNumber)}?month=${encodeURIComponent(loadedMonth)}`,
          { headers }
        );
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`${res.status} ${text}`);
        }
        const json = await res.json().catch(() => ({}));
        setDetail(json.data !== undefined ? json.data : json);
      } catch (e) {
        setDetailError(e.message || 'Failed to load detail');
      } finally {
        setDetailLoading(false);
      }
    },
    [authHeaders, loadedMonth]
  );

  const closeDetail = useCallback(() => setDetailOpen(false), []);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.ticket += Number(r.ticketSpend || 0);
          acc.content += Number(r.contentSpend || 0);
          acc.subscription += Number(r.subscriptionSpend || 0);
          acc.total += Number(r.totalSpend || 0);
          return acc;
        },
        { ticket: 0, content: 0, subscription: 0, total: 0 }
      ),
    [rows]
  );

  const spendersCount = useMemo(() => rows.filter((r) => Number(r.totalSpend) > 0).length, [rows]);

  const streamData = useMemo(
    () =>
      [
        { name: 'Tickets', value: totals.ticket, color: STREAM_COLORS.ticket },
        { name: 'Content', value: totals.content, color: STREAM_COLORS.content },
        { name: 'Subscriptions', value: totals.subscription, color: STREAM_COLORS.subscription },
      ].filter((d) => d.value > 0),
    [totals]
  );

  const topIbos = useMemo(
    () =>
      rows
        .filter((r) => Number(r.totalSpend) > 0)
        .slice(0, 10)
        .map((r) => ({
          label: truncate(r.iboName || r.iboNumber),
          ticket: Number(r.ticketSpend || 0),
          content: Number(r.contentSpend || 0),
          subscription: Number(r.subscriptionSpend || 0),
        })),
    [rows]
  );

  const hasData = loadedMonth && rows.length > 0;
  const hasSpend = totals.total > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Monthly IBO Spending
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Gross spend per registered IBO across event tickets, content and subscriptions. Tickets are
        counted in the month of the event date; content and subscriptions in the month they were
        charged. Refunds are excluded. Click a row to see the itemised breakdown.
      </Typography>

      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="Month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button variant="contained" onClick={loadReport} disabled={loading || !month}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Load report'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadReport('csv')}
            disabled={downloading !== null || !month}
          >
            {downloading === 'csv' ? 'Downloading…' : 'Download CSV'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadReport('excel')}
            disabled={downloading !== null || !month}
          >
            {downloading === 'excel' ? 'Downloading…' : 'Download Excel'}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {hasData && (
        <>
          {/* KPI cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 2.5,
            }}
          >
            <KpiCard label="Total spend" value={money(totals.total)} color={TOTAL_COLOR} icon={PaidIcon} />
            <KpiCard label="Tickets" value={money(totals.ticket)} color={STREAM_COLORS.ticket} icon={ConfirmationNumberIcon} />
            <KpiCard label="Content" value={money(totals.content)} color={STREAM_COLORS.content} icon={LibraryBooksIcon} />
            <KpiCard label="Subscriptions" value={money(totals.subscription)} color={STREAM_COLORS.subscription} icon={AutorenewIcon} />
          </Box>

          <Box sx={{ mb: 1 }}>
            <Chip
              size="small"
              label={`${formatMonthLabel(loadedMonth)} · ${rows.length} registered · ${spendersCount} with spend`}
              sx={{ bgcolor: `${theme.palette.primary.main}14`, color: theme.palette.primary.main, fontWeight: 600 }}
            />
          </Box>

          {/* Charts */}
          {hasSpend && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                mb: 2.5,
              }}
            >
              <ChartCard title="Spend by stream" theme={theme}>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={streamData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={62}
                      outerRadius={100}
                      paddingAngle={2}
                    >
                      {streamData.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Pie>
                    <RTooltip formatter={(v) => money(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Top IBOs by spend" theme={theme}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={topIbos} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-25}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} width={64} />
                    <RTooltip formatter={(v) => money(v)} />
                    <Legend />
                    <Bar dataKey="ticket" name="Tickets" stackId="s" fill={STREAM_COLORS.ticket} />
                    <Bar dataKey="content" name="Content" stackId="s" fill={STREAM_COLORS.content} />
                    <Bar
                      dataKey="subscription"
                      name="Subscriptions"
                      stackId="s"
                      fill={STREAM_COLORS.subscription}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Box>
          )}
        </>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { backgroundColor: theme.palette.grey[100], fontWeight: 700 } }}>
              <TableCell>IBO Number</TableCell>
              <TableCell>IBO Name</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Upline Platinum IBO</TableCell>
              <TableCell>Upline Platinum Name</TableCell>
              <TableCell align="right">Ticket Spend</TableCell>
              <TableCell align="right">Content Spend</TableCell>
              <TableCell align="right">Subscription Spend</TableCell>
              <TableCell align="right">Total Spend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loadedMonth ? 'No registered IBOs found.' : 'Pick a month and load the report.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const spent = Number(r.totalSpend) > 0;
                return (
                  <TableRow
                    key={r.iboNumber}
                    hover
                    onClick={() => openDetail(r.iboNumber)}
                    sx={{ cursor: 'pointer', opacity: spent ? 1 : 0.6 }}
                  >
                    <TableCell>{r.iboNumber}</TableCell>
                    <TableCell>{r.iboName || '—'}</TableCell>
                    <TableCell>
                      {r.teamName ? (
                        <Chip size="small" variant="outlined" label={r.teamName} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{r.uplinePlatinumIbo || '—'}</TableCell>
                    <TableCell>{r.uplinePlatinumIboName || '—'}</TableCell>
                    <TableCell align="right">{money(r.ticketSpend)}</TableCell>
                    <TableCell align="right">{money(r.contentSpend)}</TableCell>
                    <TableCell align="right">{money(r.subscriptionSpend)}</TableCell>
                    <TableCell align="right" sx={{ color: spent ? TOTAL_COLOR : 'text.secondary', fontWeight: 700 }}>
                      {money(r.totalSpend)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          {rows.length > 0 && (
            <TableFooter>
              <TableRow sx={{ '& td': { backgroundColor: theme.palette.grey[50], fontWeight: 700 } }}>
                <TableCell colSpan={5}>TOTAL</TableCell>
                <TableCell align="right">{money(totals.ticket)}</TableCell>
                <TableCell align="right">{money(totals.content)}</TableCell>
                <TableCell align="right">{money(totals.subscription)}</TableCell>
                <TableCell align="right" sx={{ color: TOTAL_COLOR }}>
                  {money(totals.total)}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {detailIbo}
          {detail?.iboName ? ` — ${detail.iboName}` : ''} · {formatMonthLabel(loadedMonth)}
          <IconButton onClick={closeDetail} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailError ? (
            <Alert severity="error">{detailError}</Alert>
          ) : detail ? (
            <>
              <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip label={`Tickets ${money(detail.ticketTotal)}`} sx={{ bgcolor: `${STREAM_COLORS.ticket}1A`, color: STREAM_COLORS.ticket, fontWeight: 600 }} />
                <Chip label={`Content ${money(detail.contentTotal)}`} sx={{ bgcolor: `${STREAM_COLORS.content}1A`, color: STREAM_COLORS.content, fontWeight: 600 }} />
                <Chip label={`Subscriptions ${money(detail.subscriptionTotal)}`} sx={{ bgcolor: `${STREAM_COLORS.subscription}1A`, color: '#8a6d00', fontWeight: 600 }} />
                <Chip label={`Total ${money(detail.grandTotal)}`} sx={{ bgcolor: `${TOTAL_COLOR}1A`, color: TOTAL_COLOR, fontWeight: 700 }} />
              </Stack>

              <DetailSection title="Event tickets" count={detail.events?.length || 0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Event</TableCell>
                      <TableCell>Event date</TableCell>
                      <TableCell align="right">Tickets</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.events || []).map((e, i) => (
                      <TableRow key={`${e.orderId}-${i}`}>
                        <TableCell>{e.eventName || '—'}</TableCell>
                        <TableCell>{fmtDate(e.eventDate)}</TableCell>
                        <TableCell align="right">{e.tickets ?? '—'}</TableCell>
                        <TableCell>{e.paymentMethod || '—'}</TableCell>
                        <TableCell align="right">{money(e.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DetailSection>

              <DetailSection title="Content" count={detail.content?.length || 0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Purchased</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.content || []).map((c, i) => (
                      <TableRow key={`${c.assetId || c.bundleId}-${i}`}>
                        <TableCell>{c.title || c.assetId || `Bundle #${c.bundleId}` || '—'}</TableCell>
                        <TableCell>{c.type || '—'}</TableCell>
                        <TableCell>{fmtDate(c.purchaseDate)}</TableCell>
                        <TableCell align="right">{money(c.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DetailSection>

              <DetailSection title="Subscriptions" count={detail.subscriptions?.length || 0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan</TableCell>
                      <TableCell>Charge</TableCell>
                      <TableCell>Charged on</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.subscriptions || []).map((s, i) => (
                      <TableRow key={`${s.subscriptionId}-${i}`}>
                        <TableCell>{s.planName || (s.planId ? `Plan #${s.planId}` : '—')}</TableCell>
                        <TableCell>{s.action === 'RENEWED' ? 'Renewal' : 'First charge'}</TableCell>
                        <TableCell>{fmtDate(s.chargeDate)}</TableCell>
                        <TableCell align="right">{money(s.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DetailSection>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function KpiCard({ label, value, color, icon: Icon }) {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, borderLeft: `4px solid ${color}` }}>
      <CardContent sx={{ py: 2, px: 2.5, display: 'flex', alignItems: 'center', gap: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: `${color}1A`, color }}>
          <Icon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, theme, children }) {
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function DetailSection({ title, count, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        {title} ({count})
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {count === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          None this month.
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
}
